/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type HTMLAttributes, type MouseEvent } from 'react';
import { Region } from '@/components/region';
import { SIDE_MENU_LINKS_REGION_ID } from '@/lib/page-designer/side-menu-links-page';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { cn } from '@/lib/utils';

export interface SideMenuLinksPageRegionProps extends HTMLAttributes<HTMLDivElement> {
    page: PageWithComponentData | null | undefined;
    /** Closes the side menu when the shopper activates a link inside the region. */
    onNavigate?: () => void;
}

const SIDE_MENU_LINKS_REGION_CLASS = cn(
    'side-menu-links-pd-region mx-2 mt-5 border-t border-white pt-5 space-y-4 bg-transparent shadow-none',
    '[&_[data-slot=content-block]]:gap-2 [&_[data-slot=content-block]]:shadow-none',
    '[&_[data-slot=content-block]_h2]:!text-base [&_[data-slot=content-block]_h2]:!font-medium [&_[data-slot=content-block]_h2]:!text-white',
    '[&_[data-slot=content-block]_.text-muted-foreground]:!text-white/75',
    '[&_[data-slot=content-block]_a]:!text-white',
    '[&_a]:!no-underline [&_a:hover]:!underline'
);

export function SideMenuLinksPageRegion({
    page,
    onNavigate,
    className,
    ...rest
}: SideMenuLinksPageRegionProps) {
    if (!page) {
        return null;
    }

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        if (!onNavigate) {
            return;
        }
        const target = event.target;
        if (target instanceof Element && target.closest('a')) {
            onNavigate();
        }
    };

    const linksRegion = page.regions?.find((r) => r.id === SIDE_MENU_LINKS_REGION_ID);
    const componentCount = linksRegion?.components?.length ?? 0;
    if (componentCount === 0) {
        return null;
    }

    return (
        <div
            className={cn(SIDE_MENU_LINKS_REGION_CLASS, className)}
            data-pd-side-menu-links="loaded"
            onClick={handleClick}
            {...rest}>
            <Region page={page} regionId={SIDE_MENU_LINKS_REGION_ID} />
        </div>
    );
}
